import { useMutation } from '@apollo/client'
import {
  FileUpload,
  useFileUpload,
  useSnackbar
} from '@tecsinapse/cortex-react'
import { GetItemsPorPastaQueryName } from '../data/items-pasta-query'
import { UPLOAD_ARQUIVOS } from '../data/upload-arquivos'
import { UltimosModificadosQueryName } from '../data/ultimo-modificados-query'
import { t } from '@lingui/core/macro'
import {
  ItemBiblioteca,
  UploadArquivosMutationMutation,
  UploadArquivosMutationMutationVariables
} from '@/core/types/graphql'
import { Metadata } from '@/modules/biblioteca-virtual/types/types'
import { upload } from '@/modules/biblioteca-virtual/utils/functions'
import { maxSize } from '../utils/constants'

export const useUploaderDialog = ({
  pastaAtual,
  isFolder
}: {
  pastaAtual: ItemBiblioteca
  isFolder: boolean
}) => {
  const [uploadArquivosMutation] = useMutation<
    UploadArquivosMutationMutation,
    UploadArquivosMutationMutationVariables
  >(UPLOAD_ARQUIVOS, {
    refetchQueries: [UltimosModificadosQueryName, GetItemsPorPastaQueryName]
  })

  const { snackbar } = useSnackbar()

  const onAccept = async (_accepted: FileUpload<Metadata>[]) => {
    const idPastaPai = pastaAtual.id

    const promises = (_accepted ?? []).map(async file => {
      try {
        const r = await upload(file)
        return {
          ...file,
          status: 'success',
          metadata: {
            id: String(r.id)
          }
        } as FileUpload<Metadata>
      } catch {
        return {
          ...file,
          status: 'error'
        } as FileUpload<Metadata>
      }
    })

    const files = await Promise.all(promises)

    try {
      await uploadArquivosMutation({
        variables: {
          arquivos: files
            .filter(it => it.status === 'success')
            .map(file => {
              return {
                idArquivo: String(file.metadata?.id),
                nome: file.file.name,
                relativePath: file.file.relativePath,
                idPastaPai
              }
            })
        }
      })

      snackbar.show('success', t`Arquivo(s) carregados com sucesso!`)

      onClose()
    } catch (error) {
      snackbar.show('error', (error as Error).message)
    }

    return files
  }

  const { dropzoneProps, onClose, onOpen, open } = useFileUpload({
    allowMultiple: true,
    onAccept,
    maxSize,
    preventDuplicates: true,
    onDuplicate: () => {
      snackbar.show('warning', t`Arquivo duplicado. Não foi possível enviar.`)
    },
    isFolder,
    onOpenManager: () => {
      snackbar.show(
        'warning',
        t`Não feche nem recarregue a aba do navegador durante o upload`
      )
    },
    onFileRejected: rejections => {
      rejections.forEach(({ file, errors }) => {
        if (file.size > maxSize) {
          snackbar.show('error', t`Arquivo maior que o limite`)
        } else {
          errors.forEach(error => snackbar.show('error', error.message))
        }
      })
    },
    uploadProgressText: t`Upload(s) em progresso`,
    uploadSuccessText: t`Upload(s) concluído(s) com sucesso!`,
    noClick: isFolder
  })

  return {
    open,
    onOpen,
    onClose,
    dropzoneProps
  }
}
